import React, {
  useEffect, useMemo, useState, useRef, useCallback, RefObject, Fragment, ReactElement,
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router';
import Divider from '../../../components/divider/divider.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import List from '../../../components/list/list.component';
import ListItem from '../../../components/list-item/list-item.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import ConfirmModal from '../../../components/confirm-modal/confirm-modal.component';
import StatusBar from '../../../components/status-bar/status-bar.component';
import ActionButton from '../../../components/action-button/action-button.component';
import Loader from '../../../components/loader/loader.component';
import NoContentBlock from '../../../components/no-content-block/no-content-block.component';
import ContextMenu from '../../../components/context-menu/context-menu.component';
import ContentListContainer from '../../../components/content-list-container/content-list-container.component';
import FloatingContainer from '../../../components/floating-container/floating-container.component';
import RightDrawerInternal from '../../../components/right-drawer/right-drawer.component';
import RightDrawerContainer from '../../../components/right-drawer-container/right-drawer-container.component';
import RightDrawerContentItem from '../../../components/right-drawer-content-item/right-drawer-content-item.component';
import RightDrawerHeader from '../../../components/right-drawer-header/right-drawer-header.component';
import MoreIcon from '../../../icons/more-icon';
import ConfigureIcon from '../../../icons/configure-icon';
import DeleteItemIcon from '../../../icons/delete-item-icon';
import MainService from '../../../services/main-service';
import StructureService from '../../../services/structure-service';
import ScreenService from '../../../services/screen-service';
import FormatService from '../../../services/format-service';
import useScreenSmall from '../../../hooks/use-screen-small';
import useScreenMedium from '../../../hooks/use-screen-medium';
import useCurrentUserPermissions from '../../../hooks/use-current-user-permissions';
import { toggleRightDrawer, toggleRightDrawerFirstTab } from './right-drawer.action';
import { getCommentListData } from '../comments/comments.action';
import {
  getNotificationListData, deleteNotification, deleteAllNotifications, readNotifications,
} from '../notifications/notifications.action';
import { resetFeedData, deleteFeedItem } from '../app.action';
import { NOTIFICATION_LIST_VIEW, COMMENT_LIST_VIEW } from '../../../constants/entity/permissions';
import { ROUTE_SETTINGS_NOTIFICATIONS } from '../../../constants/system/routes';
import { HTML_TAG_NAME_ANCHOR } from '../../../constants/system/html-tag-names';
import { SETTING_NAME_SHOW_NOTIFICATIONS } from '../../../constants/entity/setting-names';
import { DATA_CONTENT_ITEM_ID } from '../../../constants/system/data-attributes';
import { PAGE_LIST_DEFAULT } from '../../../constants/system/pages';
import PLATFORM_TITLE_DEFAULT from '../../../constants/system/platform-titles';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import SystemUserImage from '../../../static/images/system-user-image.png';

const RightDrawer: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const FEED_ITEMS_LIMIT: number = 3;

  const {
    isLoading,
    isRightDrawerOpen,
    isRightDrawerFirstTabOpen,
    currentUser,
    feedData,
    notifications,
    notificationsTotal,
    comments,
    commentsTotal,
    newCommentList,
    newNotificationList,
    newNotificationsTotal,
    dealData,
    portfolioIdsFilter,
    portfolioGroupIdsFilter,
  } = props;

  const navigate: ReactRouterNavigate = useNavigate();
  const location: ReactRouterLocation = useLocation();
  const dispatch: ReduxDispatch = useDispatch();
  const isMediumScreen: boolean = useScreenMedium();
  const isSmallScreen: boolean = useScreenSmall();
  const permissionList: string[] = useCurrentUserPermissions();
  const containerRef: RefObject<HTMLElement> | null = useRef(null);
  const itemListRef: RefObject<HTMLDivElement> | null = useRef(null);

  const userNameSystem: string = MainService.PLATFORM_TITLE || PLATFORM_TITLE_DEFAULT;
  const currentPathname: string = location.pathname;
  const showNotificationsSetting: Setting | null = currentUser && currentUser.settings && currentUser.settings.length
    ? (currentUser.settings.find((settingItem: Setting): boolean => (
      settingItem.name === SETTING_NAME_SHOW_NOTIFICATIONS
    )) || null) : null;
  const isNotificationFeedVisible: boolean = showNotificationsSetting
    ? FormatService.extractBooleanValue(showNotificationsSetting.value) : true;
  const isNotificationListVisible: boolean = StructureService.checkPermission(NOTIFICATION_LIST_VIEW, permissionList);
  const isNotificationTabActive: boolean = isRightDrawerFirstTabOpen && isNotificationListVisible;
  const isNotificationTabLoaded: boolean = notifications && notificationsTotal <= notifications.length;
  const isCommentTabLoaded: boolean = !!comments && commentsTotal <= comments.length;

  const [isOpenMessage, setIsOpenMessage]: [boolean, Function] = useState(false);
  const [statusMessage, setStatusMessage]: [string, Function] = useState('');
  const [menuAnchor, setMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [headerMenuAnchor, setHeaderMenuAnchor]: [HTMLElement | null, Function] = useState(null);
  const [commentPage, setCommentPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [notificationPage, setNotificationPage]: [number, Function] = useState(PAGE_LIST_DEFAULT);
  const [commentAnchorId, setCommentAnchorId]: [number | null, Function] = useState(null);
  const [notificationAnchorId, setNotificationAnchorId]: [number | null, Function] = useState(null);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen]: [boolean, Function] = useState(false);
  const [confirmModalErrorMessage, setConfirmModalErrorMessage]: [string | null, Function] = useState(null);

  const rightDrawerPermissions: string[] = useMemo((): string[] => [NOTIFICATION_LIST_VIEW, COMMENT_LIST_VIEW], []);

  const tabPermission: string = isNotificationTabActive ? NOTIFICATION_LIST_VIEW : COMMENT_LIST_VIEW;
  const tabHeaderName: string = isNotificationTabActive ? 'Notifications' : 'Comments';
  const contentItems: (Comment | Notification)[] = isNotificationTabActive ? notifications : comments;
  const dealId: number | null = (dealData && dealData.id) || null;
  const isContentLoaded: boolean = isNotificationTabActive ? isNotificationTabLoaded : isCommentTabLoaded;
  const deleteConfirmModalTitle: string = 'Delete All Notifications';
  const deleteConfirmModalText: string = 'Are you sure you want to delete all notifications?';
  const noContentBlockText: string = `There are no ${tabHeaderName.toLowerCase()} `
    + `${!isNotificationTabActive && dealData ? 'related to this deal' : 'in your feed'}`;

  const tabPermissions: string[] = useMemo((): string[] => [tabPermission], [tabPermission]);

  const onToggleRightDrawer: MouseEventHandlerFunction = useCallback((): void => {
    if (isMediumScreen) {
      dispatch(toggleRightDrawer());
    }
  }, [isMediumScreen]);

  const onFeedItemClick: Function = useCallback((): void => {
    if (!isRightDrawerOpen) {
      dispatch(toggleRightDrawer());
    }

    if (!isRightDrawerFirstTabOpen) {
      dispatch(toggleRightDrawerFirstTab(true));
    }

    if (newNotificationsTotal > 0) {
      dispatch(readNotifications());
    }

    dispatch(resetFeedData());
  }, [isRightDrawerOpen, isRightDrawerFirstTabOpen, newNotificationsTotal]);

  const onFeedItemDelete: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contentItem: ContentItem,
  ): void => {
    dispatch(deleteFeedItem({
      id: contentItem.id,
      type: contentItem.type,
    }));

    event.stopPropagation();
  }, []);

  const actionMenuNotificationItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <DeleteItemIcon />,
      text: 'Delete',
      onClick: (event: React.MouseEvent<Element, MouseEvent>, notificationId: number): void => {
        dispatch(deleteNotification({
          notificationId,
          dealId,
        }));
      },
    },
  ], [dealId]);

  const headerActionMenuNotificationItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ConfigureIcon />,
      text: 'Configure',
      onClick: (): void => {
        navigate({
          pathname: ROUTE_SETTINGS_NOTIFICATIONS,
        });
      },
    },
    {
      id: 2,
      icon: <DeleteItemIcon />,
      text: 'Delete All',
      onClick: (): void => {
        setIsDeleteConfirmModalOpen(true);
      },
    },
  ], []);

  const headerActionMenuCommentItems: ActionMenuItem[] = useMemo((): ActionMenuItem[] => [
    {
      id: 1,
      icon: <ConfigureIcon />,
      text: 'Configure',
      onClick: (): void => {
        navigate({
          pathname: ROUTE_SETTINGS_NOTIFICATIONS,
        });
      },
    },
  ], []);

  const actionMenuItems: ActionMenuItem[] = isNotificationTabActive ? actionMenuNotificationItems : [];
  const headerActionMenuItems: ActionMenuItem[] = isNotificationTabActive ? headerActionMenuNotificationItems
    : headerActionMenuCommentItems;

  const onItemClick: MouseEventItemHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
    contentItem: Comment | Notification,
  ): void => {
    const isTargetLink: boolean = event.target
      && (event.target as HTMLElement).tagName.toLowerCase() === HTML_TAG_NAME_ANCHOR;

    if (!isTargetLink && (contentItem as Comment).entityLink) {
      navigate((contentItem as Comment).entityLink);

      if (isSmallScreen) {
        onToggleRightDrawer(event);
      }
    }
  }, [isSmallScreen, isMediumScreen]);

  const getOptions: Function = useCallback((pageOption?: number, anchorIdOption?: number): RequestOptions => {
    let options: RequestOptions = {};

    options = dealId ? { ...options, dealId } : options;
    options = pageOption ? { ...options, page: pageOption } : options;
    options = portfolioIdsFilter && portfolioIdsFilter.length
      ? { ...options, portfolioIds: portfolioIdsFilter } : options;
    options = portfolioGroupIdsFilter && portfolioGroupIdsFilter.length
      ? { ...options, portfolioGroupIds: portfolioGroupIdsFilter } : options;
    options = anchorIdOption ? { ...options, anchorId: anchorIdOption } : options;

    return options;
  }, [portfolioIdsFilter, portfolioGroupIdsFilter]);

  const onScroll: MouseScrollHandlerFunction = useCallback((): void => {
    if (containerRef && containerRef.current) {
      ScreenService.infiniteScroll(
        containerRef.current.scrollTop,
        containerRef.current.clientHeight,
        itemListRef && itemListRef.current ? itemListRef.current.clientHeight : 0,
        (): void => {
          if (isNotificationTabActive) {
            if (!isNotificationTabLoaded && !isLoading) {
              dispatch(getNotificationListData(getOptions(notificationPage + PAGE_LIST_DEFAULT, notificationAnchorId)));
              setNotificationPage(notificationPage + PAGE_LIST_DEFAULT);
            }
          } else if (!isCommentTabLoaded && !isLoading) {
            dispatch(getCommentListData(getOptions(commentPage + PAGE_LIST_DEFAULT, commentAnchorId)));
            setCommentPage(commentPage + PAGE_LIST_DEFAULT);
          }
        },
        false,
        true,
      );
    }
  }, [
    containerRef, itemListRef, isNotificationTabActive, notificationPage, notificationAnchorId, commentPage,
    commentAnchorId, isNotificationTabLoaded, isCommentTabLoaded, isLoading,
  ]);

  const closeMessage: Function = useCallback((): void => {
    setIsOpenMessage(false);
    setStatusMessage('');
  }, []);

  const onDeleteAllNotificationsConfirm: Function = useCallback((): Promise<ActionCallback> => (
    new Promise((resolve: ActionResolveCallback): void => {
      dispatch(deleteAllNotifications({
        dealId: (dealData && dealData.id) || null,
      }, (response: ActionResponse): void => {
        setIsDeleteConfirmModalOpen(false);
        setConfirmModalErrorMessage(null);

        setStatusMessage('Deleted successfully');
        setIsOpenMessage(true);

        resolve(response);
      }, (exception: ServerError): void => {
        if (exception && exception.message) {
          setConfirmModalErrorMessage(exception.message);
        }
      }));
    })
  ), [dealData]);

  const closeAllNotificationsDeleteConfirmModal: Function = useCallback((): void => {
    setIsDeleteConfirmModalOpen(false);
    setConfirmModalErrorMessage(null);
  }, []);

  const openActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(event.currentTarget);
  }, []);

  const closeActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setMenuAnchor(null);

    event.stopPropagation();
  }, []);

  const openHeaderActionMenu: MouseEventHandlerFunction = useCallback((
    event: React.MouseEvent<Element, MouseEvent>,
  ): void => {
    setHeaderMenuAnchor(event.currentTarget);
  }, []);

  const closeHeaderActionMenu: MouseEventHandlerFunction = useCallback((): void => {
    setHeaderMenuAnchor(null);
  }, []);

  useEffect((): void => {
    if (isRightDrawerOpen && !StructureService.checkPermission(NOTIFICATION_LIST_VIEW, permissionList)
      && !StructureService.checkPermission(COMMENT_LIST_VIEW, permissionList)) {
      dispatch(toggleRightDrawer());
    }
  }, [currentUser]);

  useEffect((): void => {
    if (!commentAnchorId && comments && comments.length) {
      const firstComment: Comment = comments[0];

      setCommentAnchorId(firstComment.id);
    }
  }, [comments]);

  useEffect((): void => {
    if (!notificationAnchorId && notifications && notifications.length) {
      const firstNotification: Notification = notifications[0];

      setNotificationAnchorId(firstNotification.id);
    }
  }, [notifications]);

  useEffect((): void => {
    if (containerRef.current) {
      ScreenService.resetScrollPosition(containerRef.current);
    }
  }, [isRightDrawerFirstTabOpen, currentPathname]);

  useEffect((): void => {
    if (commentPage !== PAGE_LIST_DEFAULT) {
      setCommentPage(PAGE_LIST_DEFAULT);
    }
  }, [newCommentList]);

  useEffect((): void => {
    if (notificationPage !== PAGE_LIST_DEFAULT) {
      setNotificationPage(PAGE_LIST_DEFAULT);
    }
  }, [newNotificationList]);

  return (
    <CheckAccess permissions={rightDrawerPermissions}>
      <FlexContainer>
        {
          !!currentUser && (
            <RightDrawerContainer isInvisible={!isRightDrawerOpen}>
              <RightDrawerInternal
                isSwipeable={isMediumScreen}
                isOpen={isRightDrawerOpen}
                onOpen={onToggleRightDrawer}
                onClose={onToggleRightDrawer}
              >
                <CheckAccess permissions={tabPermissions}>
                  <Fragment>
                    <RightDrawerHeader>
                      <FlexContainer isTextCenter>
                        {`${tabHeaderName}${dealData && dealData.name ? ` (${dealData.name})` : ''}`}
                      </FlexContainer>
                      {
                        headerActionMenuItems && !!headerActionMenuItems.length
                        && contentItems && !!contentItems.length && (
                          <FlexContainer>
                            <ActionButton
                              withPopup
                              withSmallPadding
                              onClick={openHeaderActionMenu}
                            >
                              <MoreIcon />
                            </ActionButton>
                          </FlexContainer>
                        )
                      }
                    </RightDrawerHeader>
                    <Divider />
                    {
                      contentItems && !!contentItems.length ? (
                        <ContentListContainer isDefault ref={containerRef} onScroll={onScroll}>
                          <div ref={itemListRef}>
                            <List withOffset isLoading={!isContentLoaded && isLoading}>
                              {
                                contentItems.map((contentItem: Comment | Notification): ReactElement => (
                                  <ListItem
                                    isDefault
                                    isButton={!isNotificationTabActive && !!(contentItem as Comment).entityLink}
                                    key={contentItem.id}
                                    onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                                      onItemClick(event, contentItem);
                                    }}
                                  >
                                    <RightDrawerContentItem
                                      key={contentItem.id}
                                      isButton={!isNotificationTabActive && !!(contentItem as Comment).entityLink}
                                      isActive={isNotificationTabActive && !(contentItem as Notification).isRead}
                                      isImportant={contentItem.isImportant && !!(contentItem as Notification).subject}
                                      withLinks={isNotificationTabActive}
                                      withMentions={!isNotificationTabActive}
                                      withHashtags={!isNotificationTabActive}
                                      contentItemId={contentItem.id}
                                      contentDate={contentItem.date}
                                      contentText={contentItem.text}
                                      contentSubject={(contentItem as Notification).subject}
                                      contentImageLink={contentItem.user
                                        ? (contentItem.user.photoThumbnail || contentItem.user.photo
                                          || PersonDefaultImage)
                                        : ((contentItem.user && PersonDefaultImage) || SystemUserImage)}
                                      contentHeaderTitle={contentItem.user ? contentItem.user.fullName : userNameSystem}
                                      contentHeaderSubTitle={(
                                        !isNotificationTabActive && (contentItem as Comment).entityTypeText
                                        && (contentItem as Comment).entityName && (
                                          `added comment on ${(contentItem as Comment).entityTypeText} `
                                          + `${(contentItem as Comment).entityName}:`
                                        )
                                      )}
                                      contentLinks={contentItem.links}
                                      actionMenuItems={actionMenuItems}
                                      onLinkClick={isMediumScreen ? onToggleRightDrawer : undefined}
                                      onActionMenuOpen={openActionMenu}
                                    />
                                  </ListItem>
                                ))
                              }
                              {
                                isLoading && (
                                  <Loader isInline />
                                )
                              }
                            </List>
                          </div>
                        </ContentListContainer>
                      ) : (
                        <NoContentBlock isExtended>
                          {noContentBlockText}
                        </NoContentBlock>
                      )
                    }
                    {
                      headerActionMenuItems && (
                        <ContextMenu
                          menuAnchor={headerMenuAnchor}
                          actionMenuItems={headerActionMenuItems}
                          onClose={closeHeaderActionMenu}
                        />
                      )
                    }
                    {
                      actionMenuItems && !!actionMenuItems.length && (
                        <ContextMenu
                          menuAnchor={menuAnchor}
                          actionMenuItems={actionMenuItems}
                          dataAttributeEntityId={DATA_CONTENT_ITEM_ID}
                          onClose={closeActionMenu}
                        />
                      )
                    }
                  </Fragment>
                </CheckAccess>
              </RightDrawerInternal>
            </RightDrawerContainer>
          )
        }
        {
          !isRightDrawerOpen && !isSmallScreen && isNotificationFeedVisible && !!feedData && !!feedData.length && (
            <FloatingContainer>
              {
                feedData.slice(0, FEED_ITEMS_LIMIT).map((contentItem: ContentItem): ReactElement => (
                  <RightDrawerContentItem
                    isFeedItem
                    withLinks
                    withMentions
                    withHashtags
                    key={contentItem.id}
                    isImportant={contentItem.isImportant && !!(contentItem as Notification).subject}
                    contentHeaderTitle={contentItem.user ? contentItem.user.fullName : userNameSystem}
                    contentItemId={contentItem.id}
                    contentDate={contentItem.date}
                    contentText={contentItem.text}
                    contentSubject={(contentItem as Notification).subject}
                    contentLinks={contentItem.links}
                    contentImageLink={contentItem.user
                      ? (contentItem.user.photoThumbnail || contentItem.user.photo || PersonDefaultImage)
                      : ((contentItem.user && PersonDefaultImage) || SystemUserImage)}
                    onClick={(): void => {
                      onFeedItemClick();
                    }}
                    onDeleteItem={(event: React.MouseEvent<Element, MouseEvent>): void => {
                      onFeedItemDelete(event, contentItem);
                    }}
                  />
                ))
              }
            </FloatingContainer>
          )
        }
        <ConfirmModal
          isOpen={isDeleteConfirmModalOpen}
          title={deleteConfirmModalTitle}
          text={deleteConfirmModalText}
          errorMessage={confirmModalErrorMessage}
          onConfirm={onDeleteAllNotificationsConfirm}
          onCloseModal={closeAllNotificationsDeleteConfirmModal}
        />
        <StatusBar isOpen={isOpenMessage} message={statusMessage} onClose={closeMessage} />
      </FlexContainer>
    </CheckAccess>
  );
});

const mapState: ReduxMapStateCallback = (reducers: Reducers): ReduxMapState => {
  const {
    app,
    rightDrawer,
    deal,
    portfolioFilter,
  } = reducers;

  return {
    isLoading: rightDrawer.isLoading,
    currentUser: app.baseData && app.baseData.currentUser,
    notifications: app.baseData && app.baseData.notifications,
    notificationsTotal: app.baseData && app.baseData.notificationsTotal,
    comments: app.baseData && app.baseData.comments,
    commentsTotal: app.baseData && app.baseData.commentsTotal,
    dealData: deal.dealData && deal.dealData.dealCommonData,
    isRightDrawerFirstTabOpen: rightDrawer.isFirstTabOpen,
    isRightDrawerOpen: rightDrawer.isDrawerOpen,
    feedData: app.feedData,
    newCommentList: app.baseData && app.baseData.newCommentList,
    newNotificationList: app.baseData && app.baseData.newNotificationList,
    newNotificationsTotal: app.baseData && app.baseData.newNotificationsTotal,
    portfolioIdsFilter: portfolioFilter.portfolioIds,
    portfolioGroupIdsFilter: portfolioFilter.portfolioGroupIds,
  };
};

export default connect(mapState)(RightDrawer);
